import type { FormAnswerValue } from '../types/forms';
import type { SubmissionQuestion } from '../types/formSubmissions';

// FI-22 · Entrega 3: lógica pura del formulario guiado (FormRunner), separada del componente para
// poder probarla. Aquí no hay red ni estado: solo cómo se presenta una pregunta, qué falta en una
// sección y cómo se resume una respuesta en Revisar / Enviado.

/** Regla de forma de las respuestas: hasta seis, tarjetas con círculo; hasta doce, chips; más, desplegable. */
export type OptionPresentation = 'cards' | 'chips' | 'select';
export const MAX_OPTION_CARDS = 6;
export const MAX_OPTION_CHIPS = 12;

export function optionPresentation(optionCount: number): OptionPresentation {
  if (optionCount <= MAX_OPTION_CARDS) return 'cards';
  if (optionCount <= MAX_OPTION_CHIPS) return 'chips';
  return 'select';
}

export function isAnswered(value: FormAnswerValue): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

const DATE_FMT = new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const NUMBER_FMT = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 });

/** La respuesta en una línea, para las filas encogidas de Revisar y de Enviado. '' si no hay respuesta. */
export function answerSummary(question: Pick<SubmissionQuestion, 'type' | 'options'>, value: FormAnswerValue): string {
  if (!isAnswered(value)) return '';
  switch (question.type) {
    case 'YES_NO': return value === true ? 'Sí' : 'No';
    case 'SINGLE_SELECT': return question.options.find((option) => option.id === value)?.label ?? String(value);
    case 'MULTI_SELECT': {
      const ids = Array.isArray(value) ? value : [];
      return question.options.filter((option) => ids.includes(option.id)).map((option) => option.label).join(', ');
    }
    case 'NUMBER': return NUMBER_FMT.format(Number(value));
    case 'DATE': {
      // 'YYYY-MM-DD' es un día calendario, no un instante: se formatea como UTC para no correrlo un día.
      const date = new Date(`${String(value)}T00:00:00Z`);
      return Number.isNaN(date.getTime()) ? String(value) : DATE_FMT.format(date);
    }
    default: return String(value);
  }
}

/** Preguntas obligatorias VISIBLES de una sección que siguen sin respuesta. */
export function missingInSection(questions: SubmissionQuestion[], sectionId: string, requiredVisibleIds: Set<string>, answers: Record<string, FormAnswerValue>): SubmissionQuestion[] {
  return questions.filter((question) => question.sectionId === sectionId && requiredVisibleIds.has(question.id) && !isAnswered(answers[question.id]));
}

/** Avance del formulario: obligatorias visibles contestadas de cuántas. */
export function requiredProgress(requiredVisibleIds: Set<string>, answers: Record<string, FormAnswerValue>): { answered: number; total: number; percent: number } {
  const total = requiredVisibleIds.size;
  let answered = 0;
  requiredVisibleIds.forEach((id) => { if (isAnswered(answers[id])) answered += 1; });
  return { answered, total, percent: total === 0 ? 100 : Math.round((answered / total) * 100) };
}

/** Motivo del pie (en gris) para no poder avanzar de sección; null = se puede. */
export function sectionReason(missing: Array<Pick<SubmissionQuestion, 'text'>>): string | null {
  if (missing.length === 0) return null;
  if (missing.length === 1) return `Falta contestar «${missing[0]!.text}».`;
  return `Faltan ${missing.length} preguntas obligatorias en esta sección.`;
}

/** Motivo del pie para no poder enviar; null = se puede. */
export function submitReason(missingCount: number): string | null {
  if (missingCount === 0) return null;
  return missingCount === 1 ? 'Falta 1 pregunta obligatoria.' : `Faltan ${missingCount} preguntas obligatorias.`;
}
